import React, {
	useEffect,
	useState,
	useRef,
	useCallback,
	useReducer,
	DetailedHTMLProps,
	HTMLAttributes,
} from "react";
import useOnBlur from "./useOnBlur";
import { LatLng } from "./primitives";

type FeatureCollection = {
	features: Array<{
		geometry: {
			coordinates: [number, number];
		};
		properties: {
			label: string;
		};
	}>;
};

const token = "ge-4cfd4df91fa7174a";
const onPrompt = (
	text: string,
	center?: L.LatLng
): Promise<FeatureCollection> =>
	fetch(
		`https://api.geocode.earth/v1/autocomplete?api_key=${token}&layer=address,venue,street,intersection&focus.point.lat=${center?.lat}&focus.point.lon=${center?.lng}&text=${text}`
	).then((res) => res.json() as unknown as FeatureCollection);

export interface AutocompleteResult {
	latlng: LatLng;
	text: string;
}

type Actions =
	| {
			type: "delta";
			payload: number;
	  }
	| {
			type: "set";
			payload: number;
	  }
	| {
			type: "results";
			payload: Array<AutocompleteResult>;
	  }
	| {
			type: "hide";
	  };

interface State {
	selected: number | null;
	results: Array<AutocompleteResult> | null;
	showResults: boolean;
}

interface Props
	extends DetailedHTMLProps<HTMLAttributes<HTMLLIElement>, HTMLLIElement> {
	focus: boolean;
}

const Selectable = (props: Props) => {
	const ref = useRef<HTMLLIElement>(null);
	useEffect(() => {
		if (props.focus) {
			ref.current?.focus();
		}
	}, [props.focus]);
	return (
		<li {...props} ref={ref}>
			{props.children}
		</li>
	);
};

interface AutocompleteProps {
	name: string;
	mapRef: React.RefObject<L.Map>;
	onSelect: (res?: AutocompleteResult) => void;
	result?: AutocompleteResult;
}

const Autocomplete = ({
	name,
	mapRef,
	onSelect,
	result,
}: AutocompleteProps) => {
	const [state, dispatch] = useReducer(
		(state: State, action: Actions): State => {
			if (action.type === "set") {
				return {
					showResults: true,
					selected: action.payload,
					results: state.results,
				};
			} else if (action.type === "delta") {
				let next: number | null =
					(state.selected === null ? -1 : state.selected) + action.payload;
				if (state.results && state.results.length) {
					if (next < 0) {
						next = null;
					} else if (next > state.results.length - 1) {
						next = null;
					}
				} else {
					next = 0;
				}
				return {
					showResults: true,
					results: state.results,
					selected: next,
				};
			} else if (action.type === "hide") {
				return {
					...state,
					showResults: false,
				};
			}
			return {
				showResults: true,
				selected: state.selected,
				results: action.payload,
			};
		},
		{ selected: -1, results: null, showResults: false }
	);
	const cb = useCallback(
		async (ev: { target: HTMLInputElement }) => {
			setText(ev.target.value);
			if (ev.target.value.length === 0) {
				dispatch({
					type: "results",
					payload: [],
				});
				onSelect();
				return;
			}
			if (result) {
				onSelect();
			}
			const center = mapRef.current ? mapRef.current.getCenter() : undefined;
			const res = await onPrompt(ev.target.value, center);
			if (res.features.length) {
				dispatch({
					type: "results",
					payload: res.features.map((feat) => {
						return {
							text: feat.properties.label,
							latlng: [
								feat.geometry.coordinates[1],
								feat.geometry.coordinates[0],
							],
						};
					}),
				});
			}
		},
		[onPrompt, result]
	);
	const [text, setText] = useState("");
	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		switch (e.key) {
			case "ArrowDown":
				dispatch({ type: "delta", payload: 1 });
				break;
			case "ArrowUp":
				dispatch({ type: "delta", payload: -1 });
				break;
			case "Enter": {
				const result =
					state.results &&
					state.selected != null &&
					state.results[state.selected];
				if (result) {
					onSelect(result);
				}
				break;
			}
		}
	};
	const ref = useRef<HTMLInputElement>(null);
	const parentRef = useRef<HTMLDivElement>(null);

	useOnBlur(parentRef, () => dispatch({ type: "hide" }));

	useEffect(() => {
		if (state.selected === null) {
			ref.current?.focus();
		}
	}, [state]);

	useEffect(() => {
		if (result) {
			setText(result.text);
		}
	}, [result]);
	return (
		<div
			ref={parentRef}
			onKeyDown={onKeyDown}
			role="grid"
			className="autocompleter"
		>
			<input
				value={text}
				aria-label={name}
				autoComplete="off"
				ref={ref}
				placeholder={name}
				onChange={cb}
				name={name}
				type="search"
				spellCheck={false}
			/>
			{!result && state.showResults && state.results && (
				<ul>
					{state.results.map((res, i) => (
						<Selectable
							focus={i === state.selected}
							role="gridcell"
							tabIndex={0}
							onFocus={() => dispatch({ type: "set", payload: i })}
							onClick={() => {
								const result = state.results && state.results[i];
								if (result) {
									dispatch({ type: "set", payload: i });
									onSelect(result);
								}
							}}
						>
							{res.text}
						</Selectable>
					))}
				</ul>
			)}
		</div>
	);
};

export default Autocomplete;
